import React, {
  DetailedHTMLProps,
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  useDeferredValue,
  useImperativeHandle,
  useMemo
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef
} from '@tanstack/react-table';
import styled from 'styled-components';
import { filterNotEmpty } from '../../utils/listHelpers';
import { Uds } from '../../types';
import { DataTableBody } from './DataTableBody';
import * as DataTableTypes from './utils/DataTableTypes';
import { IDAccessor } from './utils/DataTableTypes';
import { QuerySettings, SortModel } from './utils/DataTableQueryProvider/DataTableQuery.types';
import { expandedColumn, selectionColumn } from './hooks/columnHooks';
import { DataTableProvider } from './utils/DataTableContext';
import { DataTableFiltersProvider } from './utils/DataTableFilterContext';
import { DataTableQueryProvider } from './utils/DataTableQueryProvider/DataTableQueryProvider';
import { DATA_TABLE_LIMIT, getDataTablePageCount } from './utils/getDataTableCount';
import { DataTableQuickReportsProvider } from './utils/DataTableQuickReportsContext';
import { defaultBorderStyles, noneBorderStyles } from './utils/DataTableStyledElements';
import { PluginProvider } from './plugins/impl';
import { columnToReactTableModel, initialStateToReactTableModel } from './utils/toReactTableModel';
import { defaultColumn } from './utils/DataTableHeights';
import { getRowId } from './utils/getRowId';
import { useSelectedRows } from './hooks/useSelectedRows';
import { DataTableInitialSearchModelProvider } from './utils/DataTableQueryProvider/DataTableInitialSearchModelProvider';
import { DataTableHeight, DataTableWrapper } from './DataTableHeight';
import { PropsForwarderProvider } from './plugins/impl/context/ForwarderContext';
import { DataTableTopSection } from './DataTableTopSection';

export type DataTableAPI =
  | {
      unselectAll(): void;
    }
  | undefined;

export type DataTableProps<
  D extends object = {},
  DataColumn extends object = D,
  QueryModel extends Record<string, unknown> = {},
  SortModel extends string = ''
> = Uds<
  Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'css' | 'ref'> & {
    columns: DataTableTypes.Column<DataColumn, SortModel>[];
    data: D[];
    /*! default: data-table */
    dataTest?: string;
    isFetching?: boolean;
    disableColumnResizing?: boolean;
    border?: DataTableTypes.DataTableBorder;
    rowHeight?: DataTableTypes.DataTableRowHeight;
    /*! Callback return standardized object with page, sort and query parameters
     * You can use it to update your network query
     */
    onVariablesChanged?: (variables: DataTableTypes.OnVariablesChanged<QueryModel, SortModel>) => void;
    onColumnConfigurationChanged?: (configuration: DataTableTypes.ColumnConfiguration) => void;
    querySettings?: QuerySettings;
    initialState?: DataTableTypes.InitialState | undefined;
    pageCount?: number;
    /*! If you want calculate pageCount by dataTable provide totalCount */
    totalCount?: number;
    /*! default: 100%. Table should keep height of parent element. Particularly
     * It is possible auto generate table height based on content.
     * Use this option if table shouldn't base on parent height.
     */
    tableHeight?: 'content' | string;
    /*! default: true. The paginator renders nothing for a single page, but its height is still held back from the
     * table. Set to false so a single-page table fills the space the paginator would have taken.
     */
    reservePaginatorSpaceForSinglePage?: boolean;
    /*! default: undefined/falsy. Select property name that holds unique value to enable row selection (e.g. for PersonModel it is 'uid') */
    selectable?: IDAccessor<D>;
    /*! default: undefined/falsy. Set this value if rows can be expanded */
    expandable?: boolean;
    className?: string;
    featureFlags?: DataTableTypes.DataTableFeatureFlags;
    emptyResultsMessage?: string;
  }
>;

const DataTableInner = <
  D extends object = {},
  C extends object = D,
  Q extends Record<string, unknown> = {},
  S extends string = ''
>(
  {
    columns,
    data,
    isFetching,
    disableColumnResizing,
    dataTest,
    border,
    rowHeight,
    pageCount,
    totalCount,
    tableHeight = '100%',
    reservePaginatorSpaceForSinglePage = true,
    initialState = {
      hiddenColumns: [],
      columnOrder: [],
      columnSizing: {}
    },
    selectable,
    expandable,
    onVariablesChanged,
    onColumnConfigurationChanged,
    querySettings,
    className,
    featureFlags,
    children,
    emptyResultsMessage,
    ...dataTableProps
  }: DataTableProps<D, C, Q, S>,
  ref: ForwardedRef<DataTableAPI | undefined>
): ReactElement | null => {
  const pageCountToSet = getDataTablePageCount(pageCount, totalCount, querySettings?.limit);
  const preDefinedColumnHooks = filterNotEmpty([
    !!selectable && (selectionColumn as ColumnDef<C, SortModel>),
    !!expandable && (expandedColumn as ColumnDef<C, SortModel>)
  ]);

  const hasColumnResize = !!onColumnConfigurationChanged && !disableColumnResizing && 'columnSizing' in initialState;

  const predefinedColumns = useMemo(
    () => [...preDefinedColumnHooks, ...columnToReactTableModel<D, C, S>(columns, hasColumnResize)],
    [columns, preDefinedColumnHooks, hasColumnResize]
  );

  const { clearAllSelectedRows, selectedRows, setSelectedRows, reactTableRowSelection } = useSelectedRows<C>();

  const tableInstance = useReactTable<C>({
    debugTable: false,
    columns: predefinedColumns,
    data: data as unknown as C[],
    defaultColumn,
    initialState: initialStateToReactTableModel(initialState, hasColumnResize),
    manualPagination: true,
    pageCount: pageCountToSet,
    enableColumnResizing: isFetching ? false : hasColumnResize,
    columnResizeDirection: hasColumnResize ? 'ltr' : undefined,
    columnResizeMode: hasColumnResize ? 'onChange' : undefined,
    enableRowSelection: !!selectable,
    getRowId: !!selectable ? (row) => getRowId(row, selectable as unknown as IDAccessor<C>) : undefined,
    autoResetPageIndex: false,
    autoResetExpanded: false,
    enableSorting: true,
    manualSorting: !!querySettings?.manualSortBy,
    getSubRows: !!expandable ? (row) => (row as C & { subRows: C[] }).subRows : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: reactTableRowSelection.setRowSelection,
    state: {
      rowSelection: reactTableRowSelection.rowSelection
    }
  });

  const limit = querySettings?.limit || DATA_TABLE_LIMIT;
  const ignoreQueryVariables = [...(querySettings?.hiddenVariables ?? []), 'search'];
  const isLoading = useDeferredValue(isFetching);

  useImperativeHandle(ref, () => ({
    unselectAll: clearAllSelectedRows
  }));

  const dataTableId = `${querySettings?.previousURLKey ?? ''}${dataTest ?? ''}${selectable ?? ''}`;
  return (
    <DataTableProvider
      value={{
        ...tableInstance,
        clearAllSelectedRows,
        selectedRows,
        setSelectedRows,
        isFetching: isLoading,
        border,
        totalCount,
        previousURLKey: querySettings?.previousURLKey,
        tableHeight,
        manualSortBy: !!querySettings?.manualSortBy,
        onColumnConfigurationChanged,
        resizeTableColumns: hasColumnResize,
        featureFlags,
        rowHeight,
        querySettings,
        onVariablesChanged
      }}
    >
      <PropsForwarderProvider>
        <PluginProvider>
          <DataTableHeight
            className={className}
            rowHeight={rowHeight}
            dataLength={data?.length}
            limit={limit}
            reservePaginatorSpaceForSinglePage={reservePaginatorSpaceForSinglePage}
            tableHeight={tableHeight}
          >
            <DataTableInitialSearchModelProvider
              dataTableId={dataTableId}
              setSortBy={tableInstance.setSorting}
              plugins={children}
            >
              <DataTableQueryProvider querySettings={querySettings} dataTableId={dataTableId}>
                <DataTableFiltersProvider hiddenVariables={ignoreQueryVariables}>
                  <DataTableQuickReportsProvider dataTableId={dataTableId} loading={isFetching}>
                    <DataTableTopSection />
                    <DataTableWrapper reservePaginatorSpaceForSinglePage={reservePaginatorSpaceForSinglePage}>
                      <StyledTable
                        {...dataTableProps}
                        style={{ ...dataTableProps?.style, minWidth: '100%' }}
                        role={dataTableProps.role ?? 'table'}
                        data-test={dataTest || 'data-table'}
                        $border={border}
                      >
                        <DataTableBody dataTableId={dataTableId} emptyResultsMessage={emptyResultsMessage} />
                        {children}
                      </StyledTable>
                    </DataTableWrapper>
                  </DataTableQuickReportsProvider>
                </DataTableFiltersProvider>
              </DataTableQueryProvider>
            </DataTableInitialSearchModelProvider>
          </DataTableHeight>
        </PluginProvider>
      </PropsForwarderProvider>
    </DataTableProvider>
  );
};

export const DataTable = forwardRef(DataTableInner) as unknown as <
  D extends object = {},
  C extends object = D,
  Q extends Record<string, unknown> = {},
  S extends string = ''
>(
  props: DataTableProps<D, C, Q, S> & {
    ref?: React.ForwardedRef<DataTableAPI | undefined>;
  }
) => ReturnType<typeof DataTableInner>;

const StyledTable = styled.div<{ $border?: DataTableTypes.DataTableBorder }>`
  ${({ $border }) => ($border === 'none' ? noneBorderStyles : defaultBorderStyles)};
  border-spacing: 0;
  height: 100%;
  width: min-content;
  width: 100%;
  font-size: ${({ theme }) => theme.font?.sizeSmall};
  background-color: ${(e) => e.theme.colorSurface};
  color: ${(e) => e.theme.colorOnSurface};
  .MuiTypography-body1 {
    font-size: ${({ theme }) => theme.font?.sizeSmall};
  }
`;
